import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import io from "socket.io-client";
import axios from "axios";
import PropFirm from "./PropFirm";
import LandingPage from "./LandingPage";
import ChartComponent from "./ChartComponent";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler
} from "chart.js";

// Lucide icons removed for build stability
const LayoutDashboard = () => <span>🏠</span>;
const Wallet = () => <span>💰</span>;
const TrendingUp = () => <span>📈</span>;
const LogOut = () => <span>🚪</span>;
const ShieldAlert = () => <span>🛡️</span>;
const PlusCircle = () => <span>➕</span>;
const MinusCircle = () => <span>➖</span>;
const Store = () => <span>🏪</span>;

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const API_URL = "https://nexora-engine.onrender.com/api";
const SOCKET_URL = "https://nexora-engine.onrender.com";

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000
});
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = "Bearer " + t;
  return cfg;
});

const BRAND = "Zenith Prop";
const TAGLINE = "Institutional Capital for Elite Traders";

const GlassCard = ({ children, className = "" }) => (
  <div className={`glass-card ${className}`}>{children}</div>
);

const chartOpts = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { display: false },
    y: { grid: { color: "rgba(255,255,255,0.05)" }, ticks: { color: "#8A8F98", font: { family: "JetBrains Mono" } } }
  }
};

function shortAddr(a) {
  if (!a) return "";
  return a.slice(0, 6) + "…" + a.slice(-4);
}

async function requestWallet() {
  if (!window.ethereum) throw new Error("Install MetaMask, Rabby, or Coinbase Wallet");
  const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
  if (!accounts || !accounts[0]) throw new Error("No wallet account selected");
  return accounts[0];
}

async function signZenithWallet() {
  const address = await requestWallet();
  const { data } = await api.post("/wallet/nonce", { address });
  const signature = await window.ethereum.request({ method: "personal_sign", params: [data.message, address] });
  return { address, signature, nonce: data.nonce };
}

const BrandMark = ({ size = 36, showName = true, light = false }) => (
  <div className={`flex items-center space-x-2 font-bold ${light ? "text-[#C6F94E]" : "text-white"}`} style={{ fontSize: size > 40 ? 28 : 20 }}>
    <div className="w-8 h-8 bg-[#C6F94E] rounded-lg flex items-center justify-center text-black font-black">Z</div>
    {showName && <span className="tight-spacing tracking-tighter">ZENITH <span className="text-[#8A8F98] font-medium">PROP</span></span>}
  </div>
);

function AuthPage({ setAuth }) {
  const [isLogin, setIsLogin] = useState(true);
  const [form, setForm] = useState({ username: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const finish = (res) => {
    localStorage.setItem("token", res.data.token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setAuth(res.data.user);
    const next = new URLSearchParams(window.location.search).get("next");
    if (next) navigate(next);
    else navigate(res.data.user.role === "admin" ? "/admin" : "/");
  };
  const go = async e => {
    e.preventDefault();
    setErr("");
    try {
      const res = await api.post(isLogin ? "/auth/login" : "/auth/register", form);
      finish(res);
    } catch (ex) {
      setErr(ex.response?.data?.error || "Authentication failed");
    }
  };
  const walletLogin = async () => {
    setErr(""); setBusy(true);
    try {
      const signed = await signZenithWallet();
      const res = await api.post("/auth/wallet", signed);
      finish(res);
    } catch (ex) {
      setErr(ex.response?.data?.error || ex.message || "Wallet connect failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="h-screen flex items-center justify-center bg-[#08090A] text-white">
      <GlassCard className="w-full max-w-md text-center p-10">
        <div className="flex justify-center mb-6"><BrandMark size={56} /></div>
        <h2 className="text-3xl font-bold mb-2 tight-spacing tracking-tighter">{isLogin ? "Sign In" : "Open a Seat"}</h2>
        <p className="text-[#8A8F98] mb-8">{TAGLINE}</p>
        {err && <div className="mb-4 text-red-400 text-sm bg-red-950/40 border border-red-800 rounded-xl p-3">{err}</div>}
        <form onSubmit={go} className="space-y-4">
          <input type="text" placeholder="Username" autoComplete="username" className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} required />
          <input type="password" placeholder="Password" autoComplete={isLogin ? "current-password" : "new-password"} className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
          {!isLogin && <p className="text-xs text-[#8A8F98] text-left">12+ chars with upper, lower, number and symbol.</p>}
          <button className="accent-btn w-full py-3 rounded-xl text-black font-bold">{isLogin ? "Enter Zenith" : "Create Account"}</button>
        </form>
        <button type="button" disabled={busy} onClick={walletLogin} className="ghost-btn w-full mt-3 py-3 rounded-xl disabled:opacity-50">
          {busy ? "Connecting..." : "Connect Wallet"}
        </button>
        <p className="mt-6 text-sm text-[#8A8F98] cursor-pointer hover:text-white" onClick={() => { setIsLogin(!isLogin); setErr(""); }}>
          {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
        </p>
      </GlassCard>
    </div>
  );
}

function WalletLink({ user, setAuth }) {
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [chainEth, setChainEth] = useState(null);
  const addr = user?.walletAddress;

  useEffect(() => {
    if (!addr || !window.ethereum) { setChainEth(null); return; }
    window.ethereum.request({ method: "eth_getBalance", params: [addr, "latest"] })
      .then(hex => setChainEth(parseInt(hex, 16) / 1e18))
      .catch(() => setChainEth(null));
  }, [addr]);

  const connect = async () => {
    setErr(""); setMsg(""); setBusy(true);
    try {
      const signed = await signZenithWallet();
      const res = await api.post("/wallet/connect", signed);
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setAuth(res.data.user);
      setMsg("Wallet linked.");
    } catch (ex) {
      setErr(ex.response?.data?.error || ex.message || "Connect failed");
    } finally { setBusy(false); }
  };
  const disconnect = async () => {
    setErr(""); setBusy(true);
    try {
      const res = await api.delete("/wallet/connect");
      localStorage.setItem("user", JSON.stringify(res.data.user));
      setAuth(res.data.user);
      setMsg("Wallet unlinked.");
    } catch (ex) {
      setErr(ex.response?.data?.error || "Unlink failed");
    } finally { setBusy(false); }
  };

  return (
    <GlassCard className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">On-chain wallet</div>
          {addr ? (
            <>
              <div className="font-mono text-lg">{addr}</div>
              {chainEth != null && <div className="text-sm text-[#8A8F98] font-mono">On-chain ETH: {chainEth.toFixed(6)}</div>}
              <div className="text-xs text-green-400">Linked {user.walletLinkedAt ? new Date(user.walletLinkedAt).toLocaleString() : ""}</div>
            </>
          ) : (
            <div className="text-[#8A8F98]">No wallet linked.</div>
          )}
        </div>
        <div className="flex gap-2">
          {!addr && <button disabled={busy} onClick={connect} className="px-5 py-3 rounded-xl bg-[#C6F94E] text-black font-bold disabled:opacity-50">{busy ? "Linking..." : "Connect Wallet"}</button>}
          {addr && <button disabled={busy} onClick={disconnect} className="px-5 py-3 rounded-xl bg-[#1F2124] text-white font-bold border border-[#1F2124]">Unlink</button>}
        </div>
      </div>
      {err && <div className="mt-3 text-red-400 text-sm">{err}</div>}
      {msg && <div className="mt-3 text-green-400 text-sm">{msg}</div>}
    </GlassCard>
  );
}

function FundsPanel({ user, setAuth, balances, setBalances }) {
  const [tab, setTab] = useState("add");
  const [asset, setAsset] = useState("USDT");
  const [qty, setQty] = useState("");
  const [toAddress, setToAddress] = useState(user?.walletAddress || "");
  const [note, setNote] = useState("");
  const [rows, setRows] = useState({ deposits: [], withdrawals: [] });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const load = () => api.get("/wallet/cash").then(r => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.walletAddress && !toAddress) setToAddress(user.walletAddress); }, [user, toAddress]);

  const submitAdd = async e => {
    e.preventDefault(); setErr(""); setOk("");
    try {
      await api.post("/wallet/deposit", { asset, qty: Number(qty), note });
      setQty(""); setNote("");
      setOk("Add-funds request sent. Pending admin approval.");
      load();
    } catch (ex) { setErr(ex.response?.data?.error || "Request failed"); }
  };
  const submitOut = async e => {
    e.preventDefault(); setErr(""); setOk("");
    try {
      const r = await api.post("/wallet/withdraw", { asset, qty: Number(qty), toAddress });
      setBalances(r.data.balances);
      if (setAuth) {
        const me = await api.get("/me");
        localStorage.setItem("user", JSON.stringify(me.data.user));
        setAuth(me.data.user);
      }
      setQty("");
      setOk("Withdraw requested. Pending admin approval.");
      load();
    } catch (ex) { setErr(ex.response?.data?.error || "Withdraw failed"); }
  };

  const hist = [
    ...(rows.deposits || []).map(d => ({ ...d, kind: "ADD" })),
    ...(rows.withdrawals || []).map(w => ({ ...w, kind: "WITHDRAW" }))
  ].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <GlassCard>
        <div className="flex gap-2 mb-4">
          <button type="button" onClick={() => setTab("add")} className={`px-4 py-2 rounded-xl font-bold transition-colors ${tab === "add" ? "bg-[#C6F94E] text-black" : "bg-[#1F2124] text-white"}`}>Add funds</button>
          <button type="button" onClick={() => setTab("out")} className={`px-4 py-2 rounded-xl font-bold transition-colors ${tab === "out" ? "bg-red-600 text-white" : "bg-[#1F2124] text-white"}`}>Withdraw</button>
        </div>
        {err && <div className="mb-3 text-red-400 text-sm">{err}</div>}
        {ok && <div className="mb-3 text-green-400 text-sm">{ok}</div>}
        {tab === "add" ? (
          <form onSubmit={submitAdd} className="space-y-3">
            <label className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Asset</label>
            <select value={asset} onChange={e => setAsset(e.target.value)} className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none">
              <option>USDT</option><option>BTC</option><option>ETH</option>
            </select>
            <label className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Amount</label>
            <input type="number" step="any" value={qty} onChange={e => setQty(e.target.value)} required className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none" />
            <label className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Note (optional)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. bank ref / tx id" className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none" />
            <button className="w-full py-3 rounded-xl bg-[#C6F94E] text-black font-bold">Request add funds</button>
          </form>
        ) : (
          <form onSubmit={submitOut} className="space-y-3">
            <label className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Asset</label>
            <select value={asset} onChange={e => setAsset(e.target.value)} className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none">
              <option>USDT</option><option>BTC</option><option>ETH</option>
            </select>
            <label className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Amount (available {Number(balances?.[asset] || 0).toLocaleString()})</label>
            <input type="number" step="any" value={qty} onChange={e => setQty(e.target.value)} required className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none" />
            <label className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Destination address</label>
            <input value={toAddress} onChange={e => setToAddress(e.target.value)} placeholder="0x… or chain address" required className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] text-white outline-none font-mono text-sm" />
            <button className="w-full py-3 rounded-xl bg-red-600 text-white font-bold">Request withdraw</button>
          </form>
        )}
      </GlassCard>
      <GlassCard>
        <h3 className="text-lg font-bold mb-3">Cash history</h3>
        <div className="space-y-2 max-h-80 overflow-y-auto text-sm">
          {hist.map(h => (
            <div key={h._id} className="flex justify-between p-2 rounded-lg bg-[#08090A] border border-[#1F2124]">
              <span className={h.kind === "ADD" ? "text-green-400" : "text-red-400"}>{h.kind}</span>
              <span className="font-mono">{h.qty} {h.asset}</span>
              <span className="text-[#8A8F98]">{h.status}</span>
            </div>
          ))}
          {!hist.length && <div className="text-[#8A8F98]">No requests yet.</div>}
        </div>
      </GlassCard>
    </div>
  );
}

function Sidebar({ user, setAuth, page, setPage }) {
  const nav = useNavigate();
  const logout = () => { localStorage.clear(); setAuth(null); nav("/login"); };
  const Item = ({ id, icon: Icon, label }) => (
    <div onClick={() => id === "admin" ? nav("/admin") : setPage(id)} className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-colors ${page === id ? "bg-[#C6F94E] text-black" : "hover:bg-white/5 text-[#8A8F98]"}`}>
      <Icon size={20} /><span className="font-medium">{label}</span>
    </div>
  );
  return (
    <aside className="w-64 bg-black/30 border-r border-[#1F2124] p-6 flex flex-col space-y-8">
      <BrandMark size={36} />
      <nav className="flex-1 space-y-2">
        <Item id="dash" icon={LayoutDashboard} label="Trading Desk" />
        <Item id="wallet" icon={Wallet} label="Vault" />
        <Item id="markets" icon={TrendingUp} label="Markets" />
        <div onClick={() => nav("/prop")} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-white/5 cursor-pointer text-[#C6F94E] transition-colors"><Store size={20} /><span className="font-medium">Prop desk</span></div>
        {user?.role === "admin" && <div onClick={() => nav("/admin")} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-white/5 cursor-pointer text-[#C6F94E] transition-colors"><ShieldAlert size={20} /><span className="font-medium">Admin Panel</span></div>}
      </nav>
      <div className="pt-4 border-t border-[#1F2124] space-y-4">
        <div className="text-xs text-[#8A8F98] font-mono truncate">{user?.username}</div>
        {user?.walletAddress && <div className="text-xs text-[#C6F94E] font-mono truncate">{shortAddr(user.walletAddress)}</div>}
        <button onClick={logout} className="flex items-center space-x-3 p-3 rounded-xl hover:bg-red-900/20 text-red-400 transition cursor-pointer w-full"><LogOut size={20} /><span className="font-medium">Logout</span></button>
      </div>
    </aside>
  );
}

function Dashboard({ user, setAuth }) {
  const nav = useNavigate();
  const [page, setPage] = useState("dash");
  const [selected, setSelected] = useState("BTC");
  const [assets, setAssets] = useState({});
  const [balances, setBalances] = useState(user.balances || { USDT: 0 });
  const [amount, setAmount] = useState(0.1);
  const [history, setHistory] = useState([]);
  const [candles, setCandles] = useState({});
  const [signals, setSignals] = useState({});
  const [systemStatus, setSystemStatus] = useState({ status: "Stable", color: "text-green-400" });
  const [msg, setMsg] = useState(null);
  const propBook = new URLSearchParams(window.location.search).get("book") === "prop";

  useEffect(() => {
    const s = io(SOCKET_URL, { transports: ["websocket", "polling"] });
    s.on("market-update", data => { setAssets(data); });
    s.on("candle-update", data => { setCandles(data); });
    s.on("signal-update", data => { setSignals(data); });
    s.on("control-update", data => {
      if (data.inDrawdown) { setSystemStatus({ status: "High Volatility", color: "text-red-400" }); }
      else if (data.status === "loss") { setSystemStatus({ status: "Volatile", color: "text-amber-400" }); }
      else { setSystemStatus({ status: "Stable", color: "text-green-400" }); }
    });
    if (propBook) {
      api.get("/prop/me").then(r => setBalances(r.data.balances || {})).catch(() => {});
      api.get("/prop/trades").then(r => setHistory(r.data || [])).catch(() => {});
    } else {
      api.get("/me").then(r => setBalances(r.data.user.balances)).catch(() => {});
      api.get("/trades").then(r => setHistory(r.data)).catch(() => {});
    }
    api.get("/markets").then(r => setAssets(r.data)).catch(() => {});
    return () => s.disconnect();
  }, [propBook]);

  useEffect(() => {
    api.get(`/candles/${selected}/1m`).then(r => {
      setCandles(prev => ({ ...prev, [selected]: r.data }));
    }).catch(() => {});
  }, [selected]);

  const market = assets[selected] || { price: 0, trend: "sideways" };
  const cash = balances.USDT || 0;
  const holding = balances[selected] || 0;

  const handleTrade = async type => {
    setMsg(null);
    try {
      const res = await api.post(propBook ? "/prop/trade" : "/trade", { symbol: selected, type, amount: Number(amount) });
      setBalances(res.data.balances);
      setHistory([res.data.trade, ...(history || [])]);
      setMsg({ ok: true, text: type + " filled at $" + Number(res.data.trade.price).toFixed(2) });
    } catch (err) {
      setMsg({ ok: false, text: err.response?.data?.error || "Trade failed" });
    }
  };

  return (
    <div className="h-screen flex bg-[#08090A] text-white overflow-hidden">
      <Sidebar user={user} setAuth={setAuth} page={page} setPage={setPage} />
      <main className="flex-1 p-8 overflow-y-auto space-y-6">
        {page === "dash" && (
          <>
            <header className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div>
                  <h1 className="text-3xl font-bold tight-spacing tracking-tighter">{propBook ? "Prop Desk Tape" : "Zenith Tape"}</h1>
                  <p className="text-[#8A8F98]">{propBook ? "Trading your prop seat — personal vault is separate." : "Welcome back, " + user.username}</p>
                  {propBook && <button onClick={() => nav("/prop")} className="text-[#C6F94E] text-sm mt-1 hover:underline">Back to prop desk</button>}
                </div>
                <div className="px-3 py-1 rounded-full bg-[#121316] border border-[#1F2124] flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full animate-pulse ${systemStatus.color === "text-green-400" ? "bg-green-400" : systemStatus.color === "text-amber-400" ? "bg-amber-400" : "bg-red-400"}`} />
                  <span className={`text-xs font-bold uppercase tracking-widest ${systemStatus.color}`}>{systemStatus.status}</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Available USDT</p>
                <p className="text-2xl font-mono font-bold text-[#C6F94E]">${cash.toLocaleString(undefined, { maximumFractionDigits: 2 })}</p>
                <p className="text-sm font-mono text-[#8A8F98]">{Number(holding).toFixed(4)} {selected}</p>
              </div>
            </header>
            <div className="flex gap-2 flex-wrap">
              {Object.keys(assets).map(sym => (
                <button key={sym} onClick={() => setSelected(sym)} className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${selected === sym ? "bg-[#C6F94E] text-black" : "bg-[#121316] text-[#8A8F98] border border-[#1F2124] hover:text-white"}`}>{sym}</button>
              ))}
            </div>
            <div className="grid grid-cols-12 gap-6">
              <GlassCard className="col-span-8 h-[500px] flex flex-col">
                <div className="flex justify-between mb-4">
                  <span className="text-xl font-bold">{selected} / USDT {market.halted ? <span className="text-red-400 text-sm ml-2 uppercase">Halted</span> : null}</span>
                  <span className={`font-mono font-bold ${market.trend === "bull" ? "text-green-400" : market.trend === "bear" ? "text-red-400" : "text-[#FAFAFA]"}`}>
                    ${Number(market.price || 0).toFixed(2)} ({market.trend})
                  </span>
                </div>
                <div className="flex-1 overflow-hidden">
                  <ChartComponent symbol={selected} data={candles[selected] || []} />
                </div>
              </GlassCard>
              <GlassCard className="col-span-4 space-y-6">
                <h3 className="text-xl font-bold">Quick Trade</h3>
                <label className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Amount ({selected})</label>
                <input type="number" step="any" value={amount} onChange={e => setAmount(e.target.value)} className="w-full p-3 rounded-xl bg-[#08090A] border border-[#1F2124] outline-none focus:border-[#C6F94E] text-lg font-mono" />
                <p className="text-xs text-[#8A8F98]">Est. ${((Number(amount) || 0) * (market.price || 0)).toFixed(2)} + 0.1% fee</p>
                <div className="grid grid-cols-2 gap-4">
                  <button disabled={market.halted} onClick={() => handleTrade("BUY")} className="py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-500 transition flex items-center justify-center space-x-2 disabled:opacity-40"><PlusCircle size={18} /><span>BUY</span></button>
                  <button disabled={market.halted} onClick={() => handleTrade("SELL")} className="py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-500 transition flex items-center justify-center space-x-2 disabled:opacity-40"><MinusCircle size={18} /><span>SELL</span></button>
                </div>
                {msg && <div className={`text-sm rounded-xl p-3 ${msg.ok ? "bg-green-950/40 text-green-400" : "bg-red-950/40 text-red-400"}`}>{msg.text}</div>}
                <div className="pt-2">
                  <h4 className="text-sm font-semibold text-[#8A8F98] mb-4">History</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(history || []).slice(0, 12).map((t, i) => (
                      <div key={t._id || i} className="flex justify-between p-2 rounded-lg bg-[#08090A] border-l-4 border-[#C6F94E] text-xs font-mono">
                        <span className={t.type === "BUY" ? "text-green-400" : "text-red-400"}>{t.type}</span>
                        <span>{t.amount} {t.symbol}</span>
                        <span className="text-[#8A8F98]">${Number(t.total || 0).toFixed(2)}</span>
                      </div>
                    ))}
                    {(!history || !history.length) && <div className="text-[#8A8F98] text-xs">No trades yet</div>}
                  </div>
                </div>
              </GlassCard>
            </div>
            <GlassCard>
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <TrendingUp size={20} className="text-[#C6F94E]" />
                AI Market Intelligence
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(signals).map(([sym, sig]) => (
                  <div key={sym} className="p-4 rounded-xl bg-[#08090A] border border-[#1F2124] flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">{sym}</span>
                      <span className={`text-xs font-bold px-2 py-1 rounded ${sig.signal === "Buy" ? "bg-green-900 text-green-300" : sig.signal === "Sell" ? "bg-red-900 text-red-300" : "bg-slate-800 text-slate-400"}`}>
                        {sig.signal}
                      </span>
                    </div>
                    <div className="text-sm text-[#8A8F98]">{sig.reason}</div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-[#1F2124] rounded-full overflow-hidden">
                        <div className={`h-full transition-all ${sig.signal === "Buy" ? "bg-green-500" : sig.signal === "Sell" ? "bg-red-500" : "bg-slate-500"}`} style={{ width: `${sig.strength * 100}%` }} />
                      </div>
                      <span className="text-[10px] text-[#8A8F98] font-mono">{Math.round(sig.strength * 100)}% conf</span>
                    </div>
                  </div>
                ))}
                {Object.keys(signals).length === 0 && <div className="text-[#8A8F98] text-sm col-span-3 py-4 text-center">Analyzing markets...</div>}
              </div>
            </GlassCard>
          </>
        )}
        {page === "wallet" && (
          <>
            <h1 className="text-3xl font-bold tight-spacing tracking-tighter">Vault</h1>
            <WalletLink user={user} setAuth={setAuth} />
            <FundsPanel user={user} setAuth={setAuth} balances={balances} setBalances={setBalances} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(balances).map(([k, v]) => (
                <GlassCard key={k} className="p-6"><div className="text-[#8A8F98] text-xs uppercase font-bold tracking-widest">{k}</div><div className="text-2xl font-mono font-bold mt-2">{Number(v).toLocaleString(undefined, { maximumFractionDigits: 6 })}</div></GlassCard>
              ))}
            </div>
            <p className="text-xs text-[#8A8F98] mt-4">Zenith paper balances above. Connecting a chain wallet proves ownership; it does not move on-chain funds into this book.</p>
          </>
        )}
        {page === "markets" && (
          <>
            <h1 className="text-3xl font-bold tight-spacing tracking-tighter">Markets</h1>
            <GlassCard className="overflow-hidden p-0">
              <table className="w-full text-left">
                <thead className="bg-[#121316] text-[#8A8F98] text-xs uppercase tracking-widest font-bold">
                  <tr><th className="p-4">Symbol</th><th className="p-4">Price</th><th className="p-4">Trend</th><th className="p-4">High</th><th className="p-4">Low</th><th className="p-4"></th></tr>
                </thead>
                <tbody className="text-sm">
                  {Object.entries(assets).map(([sym, a]) => (
                    <tr key={sym} className="border-b border-[#1F2124] cursor-pointer hover:bg-[#1F2124]/50 transition-colors" onClick={() => { setSelected(sym); setPage("dash"); }}>
                      <td className="p-4 font-bold">{sym}</td>
                      <td className="p-4 font-mono">${Number(a.price).toFixed(2)}</td>
                      <td className={`p-4 font-medium ${a.trend === "bull" ? "text-green-400" : a.trend === "bear" ? "text-red-400" : "text-[#FAFAFA]"}`}>{a.trend}{a.halted ? " / HALT" : ""}</td>
                      <td className="p-4 font-mono">${Number(a.high || a.price).toFixed(2)}</td>
                      <td className="p-4 font-mono">${Number(a.low || a.price).toFixed(2)}</td>
                      <td className="p-4 text-[#C6F94E] text-sm font-bold">Trade</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </GlassCard>
          </>
        )}
      </main>
    </div>
  );
}

function AdminPanel({ setAuth }) {
  const [users, setUsers] = useState([]);
  const [botStatus, setBotStatus] = useState({ isActive: false, buyThreshold: 60000, sellThreshold: 70000 });
  const [sym, setSym] = useState("BTC");
  const [assets, setAssets] = useState({});
  const [trades, setTrades] = useState([]);
  const [ctrl, setCtrl] = useState(null);
  const [cash, setCash] = useState({ deposits: [], withdrawals: [] });
  const [prop, setProp] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [u, b, m, t, c, k, p] = await Promise.all([
        api.get("/admin/users"), api.get("/admin/bot-settings"), api.get("/markets"),
        api.get("/admin/trades"), api.get("/admin/control"), api.get("/admin/cash"),
        api.get("/admin/prop")
      ]);
      setUsers(u.data);
      setBotStatus(b.data || botStatus);
      setAssets(m.data);
      setTrades(t.data);
      setCtrl(c.data);
      setCash(k.data);
      setProp(p.data);
    } catch (e) { console.error(e); }
  };
  useEffect(() => {
    load();
    const s = io(SOCKET_URL, { transports: ["websocket", "polling"] });
    s.on("market-update", setAssets);
    s.on("control-update", setCtrl);
    s.on("admin_event", load);
    return () => s.disconnect();
  }, []);

  const patchCtrl = async body => {
    try {
      const r = await api.patch("/admin/control", body);
      setCtrl(r.data);
    } catch (e) { alert("Failed to update profit guard"); }
  };
  const updateBot = async (field, value) => {
    try {
      const res = await api.patch("/admin/bot-settings", { [field]: value });
      setBotStatus(res.data);
    } catch (e) { alert("Failed to update bot"); }
  };
  const patchMarket = async body => {
    await api.patch("/admin/market", { symbol: sym, ...body }).catch(() => alert("Market update failed"));
    load();
  };
  const adjust = async u => {
    const asset = window.prompt("Asset", "USDT"); if (!asset) return;
    const amount = window.prompt("Set balance to", String(u.balances?.[asset] || 0)); if (amount == null) return;
    await api.patch("/admin/user-balance", { userId: u._id, symbol: asset, amount: Number(amount) });
    load();
  };
  const [redCode, setRedCode] = useState("");
  const fireCodeRed = async () => {
    if (!window.confirm("CODE RED will make the site disappear. Continue?")) return;
    try {
      await api.post("/admin/code-red", { code: redCode });
    } catch (e) {}
    localStorage.clear();
    document.title = "";
    window.location.replace("about:blank");
  };

  return (
    <div className="h-screen flex bg-[#08090A] text-white overflow-hidden">
      <aside className="w-64 bg-black/30 border-r border-[#1F2124] p-6 flex flex-col space-y-8">
        <BrandMark size={36} light />
        <nav className="flex-1 space-y-2">
          <div className="p-3 rounded-xl bg-[#C6F94E] text-black font-bold">Profit Guard</div>
          <div onClick={() => nav("/prop")} className="p-3 rounded-xl hover:bg-white/5 cursor-pointer text-[#C6F94E] transition-colors">Prop desk</div>
          <div onClick={() => nav("/")} className="p-3 rounded-xl hover:bg-white/5 cursor-pointer text-white/40 transition-colors">Back to tape</div>
        </nav>
        <button onClick={() => { localStorage.clear(); setAuth(null); nav("/login"); }} className="p-3 rounded-xl hover:bg-red-900/20 text-red-400 transition cursor-pointer flex items-center space-x-2"><LogOut size={20} /><span className="font-medium">Logout</span></button>
      </aside>
      <main className="flex-1 p-8 overflow-y-auto space-y-8">
        <GlassCard>
          <h3 className="text-xl font-bold mb-4">House Trading (Admin Only)</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-xs text-[#8A8F98]">Asset</label>
                <select value={sym} onChange={e => setSym(e.target.value)} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]">
                  {Object.keys(assets).map(s => <option key={s} value={s}>{s} — ${Number(assets[s].price).toFixed(2)} {assets[s].halted ? "(HALTED)" : ""}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="block text-xs text-[#8A8F98]">Amount</label>
                <input type="number" step="any" id="house-qty" placeholder="0.1" className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={async () => {
                const qty = document.getElementById("house-qty").value;
                if (!qty) return alert("Enter amount");
                try { await api.post("/admin/house-trade", { symbol: sym, type: "BUY", amount: qty }); alert("House BUY filled"); } catch (e) { alert(e.response?.data?.error || "Trade failed"); }
              }} className="py-2 rounded-xl bg-green-700 text-white font-bold hover:bg-green-600 transition">House BUY</button>
              <button onClick={async () => {
                const qty = document.getElementById("house-qty").value;
                if (!qty) return alert("Enter amount");
                try { await api.post("/admin/house-trade", { symbol: sym, type: "SELL", amount: qty }); alert("House SELL filled"); } catch (e) { alert(e.response?.data?.error || "Trade failed"); }
              }} className="py-2 rounded-xl bg-red-700 text-white font-bold hover:bg-red-600 transition">House SELL</button>
            </div>
            <p className="text-[10px] text-[#8A8F98] italic">Executing trades via systembot account to offset house exposure.</p>
          </div>
        </GlassCard>
        <GlassCard className="border border-red-800">
          <h3 className="text-xl font-bold text-red-400 mb-2">CODE RED</h3>
          <p className="text-sm text-[#8A8F98] mb-3">Complete shutdown. Site disappears. No operations.</p>
          <div className="flex gap-2">
            <input type="password" autoComplete="off" value={redCode} onChange={e => setRedCode(e.target.value)} placeholder="Enter shutdown code" className="flex-1 p-3 rounded-xl bg-[#08090A] border border-red-900 text-white outline-none focus:border-red-500" />
            <button type="button" onClick={fireCodeRed} className="px-6 py-3 rounded-xl bg-red-700 text-white font-bold">CODE RED</button>
          </div>
        </GlassCard>
        {prop && (
          <GlassCard>
            <div className="flex items-center justify-between mb-4">
              <div className="space-y-1">
                <div className="text-xs text-[#8A8F98] uppercase tracking-widest">Funding Management</div>
                <h3 className="text-2xl font-bold">Zenith Prop</h3>
              </div>
              <div className="flex gap-2">
                <button onClick={() => api.patch("/admin/prop", { enabled: !prop.enabled }).then(r => setProp({ ...prop, ...r.data }))} className={`ghost-btn px-3 py-1 rounded-lg text-xs ${prop.enabled ? "text-[#C6F94E]" : ""}`}>{prop.enabled ? "Desk ON" : "Desk OFF"}</button>
                <button onClick={() => api.patch("/admin/prop", { autoWatch: !prop.autoWatch }).then(r => setProp({ ...prop, ...r.data }))} className="ghost-btn px-3 py-1 rounded-lg text-xs">{prop.autoWatch ? "Watchdog ON" : "Watchdog OFF"}</button>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[#8A8F98] text-left border-b border-[#1F2124]">
                <tr><th className="pb-2">Trader</th><th className="pb-2">Tier</th><th className="pb-2">Stage</th><th className="pb-2">Equity</th><th className="pb-2">Status</th><th className="pb-2"></th></tr>
              </thead>
              <tbody className="text-sm">
                {(prop.challenges || []).map(c => (
                  <tr key={c.id} className="border-b border-[#1F2124] hover:bg-white/5 transition-colors">
                    <td className="py-2 font-medium">{c.username}</td>
                    <td className="py-2">{c.tier}</td>
                    <td className="py-2">{c.stage}</td>
                    <td className="py-2 text-[#C6F94E] font-mono">${Number(c.currentEquity).toLocaleString()}</td>
                    <td className="py-2">{c.status}</td>
                    <td className="flex gap-2 py-2">
                      <button className="text-[#C6F94E] hover:underline" onClick={() => api.post("/admin/prop/promote", { id: c.id }).then(load)}>Promote</button>
                      <button className="text-red-400 hover:underline" onClick={() => api.post("/admin/prop/fail", { id: c.id, reason: "Admin" }).then(load)}>Fail</button>
                    </td>
                  </tr>
                ))}
                {!(prop.challenges || []).length && <tr><td colSpan={6} className="py-3 text-center text-[#8A8F98]">No prop seats yet</td></tr>}
              </tbody>
            </table>
            <div className="mt-6 space-y-2">
              <div className="text-xs font-bold text-[#8A8F98] uppercase mb-2">Recent Payouts</div>
              {(prop.payouts || []).slice(0, 8).map(p => (
                <div key={p._id} className="flex justify-between text-sm p-2 rounded-lg bg-[#08090A] border border-[#1F2124]">
                  <span>{p.username} <span className="text-[#C6F94E]">${p.qty}</span> <span className="text-[#8A8F98] text-xs ml-2">{p.status}</span></span>
                  {p.status === "pending" && (
                    <span className="flex gap-2">
                      <button className="text-green-400 hover:underline" onClick={() => api.post("/admin/prop/payouts/" + p._id + "/approve").then(load)}>Approve</button>
                      <button className="text-red-400 hover:underline" onClick={() => api.post("/admin/prop/payouts/" + p._id + "/reject").then(load)}>Reject</button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </GlassCard>
        )}
        <h1 className="text-3xl font-bold tight-spacing tracking-tighter">Profit Guard</h1>
        <p className="text-[#8A8F98] -mt-4 mb-6">Real-time house P&amp;L orchestration. Auto-steer graphs and halt if the book goes red.</p>
        {ctrl && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <GlassCard>
                <div className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">House P&amp;L</div>
                <div className={`text-3xl font-mono font-bold mt-1 ${ctrl.housePnl >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {ctrl.housePnl >= 0 ? "+" : ""}${Number(ctrl.housePnl).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
                <div className={`mt-2 text-[10px] font-bold uppercase tracking-tighter ${ctrl.inDrawdown ? "text-red-400" : ctrl.status === "profit" ? "text-green-400" : "text-amber-400"}`}>{ctrl.inDrawdown ? "DRAWDOWN — GUARD ACTIVE" : ctrl.status === "profit" ? "IN PROFIT" : "BELOW TARGET"}</div>
              </GlassCard>
              <GlassCard>
                <div className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Trader P&amp;L</div>
                <div className="text-3xl font-mono font-bold mt-1">${Number(ctrl.userPnl).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className="text-xs text-[#8A8F98] mt-2">{ctrl.userCount} book accounts</div>
              </GlassCard>
              <GlassCard>
                <div className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Fees captured</div>
                <div className="text-3xl font-mono font-bold mt-1 text-[#C6F94E]">${Number(ctrl.fees).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className="text-xs text-[#8A8F98] mt-2">0.1% on fills</div>
              </GlassCard>
              <GlassCard>
                <div className="text-xs text-[#8A8F98] uppercase font-bold tracking-widest">Auto profit</div>
                <button onClick={() => patchCtrl({ autoProfit: !ctrl.settings.autoProfit })} className={`mt-3 w-full py-2 rounded-xl font-bold transition-colors ${ctrl.settings.autoProfit ? "bg-green-600 text-white" : "bg-[#1F2124] text-[#8A8F98]"}`}>
                  {ctrl.settings.autoProfit ? "ON — guarding" : "OFF"}
                </button>
              </GlassCard>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <GlassCard className="h-[320px]">
                <h3 className="text-lg font-bold mb-2">House profit graph</h3>
                <div className="h-64">
                  <Line data={{
                    labels: (ctrl.pnlHistory || []).map((_, i) => i),
                    datasets: [{
                      data: (ctrl.pnlHistory || []).map(p => p.housePnl),
                      borderColor: (ctrl.housePnl || 0) >= 0 ? "#C6F94E" : "#FF4757",
                      backgroundColor: "rgba(198, 249, 78, 0.05)",
                      fill: true, tension: 0.35, pointRadius: 0, borderWidth: 2
                    }]
                  }} options={chartOpts} />
                </div>
              </GlassCard>
              <GlassCard>
                <h3 className="text-lg font-bold mb-3">Guard settings</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="space-y-1">
                    <label className="text-xs text-[#8A8F98]">Target house P&amp;L ($)</label>
                    <input type="number" defaultValue={ctrl.settings.targetHousePnl} key={"t"+ctrl.settings.targetHousePnl} onBlur={e => patchCtrl({ targetHousePnl: Number(e.target.value) })} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-[#8A8F98]">Halt if below ($)</label>
                    <input type="number" defaultValue={ctrl.settings.minHousePnl} key={"m"+ctrl.settings.minHousePnl} onBlur={e => patchCtrl({ minHousePnl: Number(e.target.value) })} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-[#8A8F98]">Graph aggressiveness (0–1)</label>
                    <input type="number" step="0.05" min="0" max="1" defaultValue={ctrl.settings.aggressiveness} key={"a"+ctrl.settings.aggressiveness} onBlur={e => patchCtrl({ aggressiveness: Number(e.target.value) })} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-[#8A8F98]">Protect mode</label>
                    <select value={ctrl.settings.protectMode} onChange={e => patchCtrl({ protectMode: e.target.value })} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]">
                      <option value="steer">Steer graphs only</option>
                      <option value="halt">Halt only</option>
                      <option value="both">Steer + halt</option>
                    </select>
                  </div>
                </div>
                {ctrl.inDrawdown && (
                  <button onClick={() => api.post("/admin/control/resume-all").then(r => setCtrl(r.data))} className="mt-4 w-full py-2 rounded-xl bg-green-600 text-white font-bold">Resume all markets</button>
                )}
                <p className="text-xs text-[#8A8F98] mt-4">When auto is on, graphs fade crowded user positions so the house stays at or above target. If P&amp;L drops through the floor, trading halts.</p>
              </GlassCard>
            </div>
            <GlassCard>
              <h3 className="text-lg font-bold mb-3">Net user exposure (what graphs will fade)</h3>
              <div className="flex gap-3 flex-wrap">
                {Object.keys(ctrl.exposure || {}).length === 0 && <span className="text-[#8A8F98] text-sm">No open user inventory</span>}
                {Object.entries(ctrl.exposure || {}).map(([k, v]) => (
                  <div key={k} className="px-4 py-3 rounded-xl bg-[#08090A] border border-[#1F2124]">
                    <div className="text-xs text-[#8A8F98]">{k}</div>
                    <div className={`font-mono font-bold ${v > 0 ? "text-amber-400" : "text-sky-400"}`}>{Number(v).toFixed(4)} {v > 0 ? "net long → bear graph" : "net short → bull graph"}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 max-h-32 overflow-y-auto space-y-1 text-xs text-[#8A8F98] font-mono">
                {(ctrl.lastActions || []).slice(0, 8).map((a, i) => (
                  <div key={i}><span className="text-[#C6F94E]">{a.type}</span> {a.symbol} {a.trend || ""} — {a.reason}</div>
                ))}
              </div>
            </GlassCard>
          </>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard>
            <h3 className="text-xl font-bold mb-4">Market Override</h3>
            <select value={sym} onChange={e => setSym(e.target.value)} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white mb-4 outline-none focus:border-[#C6F94E]">
              {Object.keys(assets).map(s => <option key={s} value={s}>{s} — ${Number(assets[s].price).toFixed(2)} {assets[s].halted ? "(HALTED)" : ""}</option>)}
            </select>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="block text-xs text-[#8A8F98]">Price Control ($)</label>
                <input type="number" step="any" defaultValue={assets[sym]?.price} key={sym + "-px"} onBlur={e => { if (e.target.value) patchMarket({ price: parseFloat(e.target.value) }); }} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" />
              </div>
              <div className="space-y-1">
                <label className="block text-xs text-[#8A8F98]">Market Trend</label>
                <select value={assets[sym]?.trend || "sideways"} onChange={e => patchMarket({ trend: e.target.value })} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]">
                  <option value="sideways">Sideways</option>
                  <option value="bull">Bull (Up)</option>
                  <option value="bear">Bear (Down)</option>
                </select>
              </div>
              <button onClick={() => patchMarket({ halted: !assets[sym]?.halted })} className={`w-full py-2 rounded-xl font-bold transition-colors ${assets[sym]?.halted ? "bg-green-700 text-white" : "bg-red-700 text-white"}`}>
                {assets[sym]?.halted ? "Resume trading" : "Halt trading"}
              </button>
            </div>
          </GlassCard>
          <GlassCard>
            <h3 className="text-xl font-bold mb-4">Automated Trading Bot</h3>
            <div className="flex items-center justify-between p-4 bg-[#08090A] rounded-xl mb-4 border border-[#1F2124]">
              <span className="text-sm font-medium">Bot Active State</span>
              <button onClick={() => updateBot("isActive", !botStatus.isActive)} className={`px-4 py-2 rounded-lg font-bold transition-colors ${botStatus.isActive ? "bg-green-600 text-white" : "bg-[#1F2124] text-[#8A8F98]"}`}>{botStatus.isActive ? "ON" : "OFF"}</button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-xs text-[#8A8F98]">Buy Threshold ($)</label>
                <input type="number" value={botStatus.buyThreshold || 0} onChange={e => setBotStatus({ ...botStatus, buyThreshold: parseFloat(e.target.value) })} onBlur={e => updateBot("buyThreshold", parseFloat(e.target.value))} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" />
              </div>
              <div className="space-y-1">
                <label className="block text-xs text-[#8A8F98]">Sell Threshold ($)</label>
                <input type="number" value={botStatus.sellThreshold || 0} onChange={e => setBotStatus({ ...botStatus, sellThreshold: parseFloat(e.target.value) })} onBlur={e => updateBot("sellThreshold", parseFloat(e.target.value))} className="w-full p-2 rounded bg-[#08090A] border border-[#1F2124] text-white outline-none focus:border-[#C6F94E]" />
              </div>
            </div>
            <p className="text-xs text-[#8A8F98] mt-4">Bot trades BTC against these thresholds. Settings persist in MongoDB.</p>
          </GlassCard>
        </div>
        <GlassCard>
          <h3 className="text-xl font-bold mb-4">Cash desk — add / withdraw</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-sm">
            <div className="space-y-4">
              <div className="text-[#8A8F98] font-bold uppercase tracking-widest text-xs">Deposits</div>
              {(cash.deposits || []).slice(0, 12).map(d => (
                <div key={d._id} className="flex items-center justify-between py-2 border-b border-[#1F2124]">
                  <span className="font-medium">{d.username} <span className="text-[#C6F94E]">+{d.qty} {d.asset}</span> <span className="text-[#8A8F98] text-xs ml-2">{d.status}</span></span>
                  {d.status === "pending" && (
                    <span className="flex gap-2 shrink-0">
                      <button className="text-green-400 hover:underline" onClick={() => api.post("/admin/deposits/" + d._id + "/approve").then(load)}>Approve</button>
                      <button className="text-red-400 hover:underline" onClick={() => api.post("/admin/deposits/" + d._id + "/reject").then(load)}>Reject</button>
                    </span>
                  )}
                </div>
              ))}
              {!(cash.deposits || []).length && <div className="text-[#8A8F98]">None</div>}
            </div>
            <div className="space-y-4">
              <div className="text-[#8A8F98] font-bold uppercase tracking-widest text-xs">Withdrawals</div>
              {(cash.withdrawals || []).slice(0, 12).map(w => (
                <div key={w._id} className="flex items-center justify-between py-2 border-b border-[#1F2124] gap-2">
                  <span className="truncate">{w.username} <span className="text-red-400">−{w.qty} {w.asset}</span> <span className="text-[#8A8F98] text-xs ml-2">{w.status}</span></span>
                  {w.status === "pending" && (
                    <span className="flex gap-2 shrink-0">
                      <button className="text-green-400 hover:underline" onClick={() => api.post("/admin/withdrawals/" + w._id + "/approve").then(load)}>Approve</button>
                      <button className="text-red-400 hover:underline" onClick={() => api.post("/admin/withdrawals/" + w._id + "/reject").then(load)}>Reject</button>
                    </span>
                  )}
                </div>
              ))}
              {!(cash.withdrawals || []).length && <div className="text-[#8A8F98]">None</div>}
            </div>
          </div>
        </GlassCard>
        <GlassCard>
          <h3 className="text-xl font-bold mb-4">User Database</h3>
          <table className="w-full text-left">
            <thead className="text-[#8A8F98] text-xs uppercase tracking-widest font-bold border-b border-[#1F2124]">
              <tr><th className="pb-2">Username</th><th className="pb-2">Role</th><th className="pb-2">USDT</th><th className="pb-2">BTC</th><th className="pb-2">P&amp;L</th><th className="pb-2">Action</th></tr>
            </thead>
            <tbody className="text-sm">
              {users.map(u => {
                const row = (ctrl?.leaderboard || []).find(x => x._id === u._id);
                const pnl = row ? row.pnl : 0;
                return (
                <tr key={u._id} className="border-b border-[#1F2124] hover:bg-white/5 transition-colors">
                  <td className="py-3 font-medium">{u.username}</td>
                  <td className="py-3"><span className="px-2 py-1 rounded bg-[#1F2124] text-xs text-[#8A8F98]">{u.role}</span></td>
                  <td className="py-3 font-mono">${Number(u.balances?.USDT || 0).toLocaleString()}</td>
                  <td className="py-3 font-mono">{Number(u.balances?.BTC || 0).toFixed(4)}</td>
                  <td className={`py-3 font-mono ${pnl >= 0 ? "text-green-400" : "text-red-400"}`}>{u.role === "admin" ? "—" : ((pnl >= 0 ? "+" : "") + "$" + Number(pnl).toFixed(2))}</td>
                  <td className="py-3"><button className="text-[#C6F94E] hover:underline" onClick={() => adjust(u)}>Adjust</button></td>
                </tr>
              );})}
              {(!users || !users.length) && <tr><td colSpan={6} className="py-3 text-center text-[#8A8F98]">No users found</td></tr>}
            </tbody>
          </table>
        </GlassCard>
        <GlassCard>
          <h3 className="text-xl font-bold mb-4">Recent fills</h3>
          <div className="space-y-2 max-h-56 overflow-y-auto text-sm font-mono">
            {trades.slice(0, 30).map(t => (
              <div key={t._id} className="flex justify-between text-[#8A8F98] p-2 rounded-lg bg-[#08090A] border border-[#1F2124]">
                <span>{t.type} {t.amount} {t.symbol}</span>
                <span className="text-white">${Number(t.total || 0).toFixed(2)}</span>
              </div>
            ))}
            {(!trades || !trades.length) && <div className="text-[#8A8F98] text-center py-4">No fills yet</div>}
          </div>
        </GlassCard>
      </main>
    </div>
  );
}

export default function App() {
  const [auth, setAuth] = useState(() => {
    try { return JSON.parse(localStorage.getItem("user") || "null"); } catch { return null; }
  });
  const [gone, setGone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleGlobalError = (msg, src, lin, col, err) => {
      setError(`${msg} at ${src}:${lin}:${col}\\n${err?.stack || ""}`);
      return true;
    };
    window.onerror = handleGlobalError;
    window.onunhandledrejection = (e) => {
      setError("Unhandled Promise Rejection: " + e.reason);
    };

    fetch(API_URL + "/health")
      .then(r => { if (r.status === 404) setGone(true); })
      .catch(e => { console.error("Health check failed", e); });

    return () => {
      window.onerror = null;
      window.onunhandledrejection = null;
    };
  }, []);

  if (error) {
    return (
      <div style={{ background: "#000", color: "red", minHeight: "100vh", padding: "20px", fontFamily: "monospace" }}>
        <h1 style={{ color: "red" }}>Critical Crash</h1>
        <pre style={{ color: "white", whiteSpace: "pre-wrap" }}>{error}</pre>
        <button onClick={() => window.location.reload()} style={{ padding: "10px", color: "white", background: "red", border: "none", cursor: "pointer" }}>Reload Page</button>
      </div>
    );
  }

  if (gone) {
    document.title = "";
    return <div style={{ background: "#fff", minHeight: "100vh" }} />;
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<AuthPage setAuth={setAuth} />} />
        <Route path="/" element={!auth ? <LandingPage /> : <PropFirm user={auth} setAuth={setAuth} />} />
        <Route path="/admin" element={auth?.role === "admin" ? <AdminPanel setAuth={setAuth} /> : <Navigate to="/login" />} />
      </Routes>
    </Router>
  );
}
